/**
 * @file TagsPage.tsx
 * @description Теги каталога (phase08.5; phase19.31 — каркас справочника (б)
 * разбора B).
 *
 * Компактный список до 960px: «Витаминный · 4 товара · ⋯». «+ Тег» —
 * главное действие в верхней панели, ввод — окном (`TagEditDialog`), а не
 * полем над списком. Удаление — из «⋯» с подтверждением, а не крестиком в
 * один клик. Открытый на правку тег — в адресе `?edit=`.
 */

import { Pencil, Plus, Tags, Trash2 } from "lucide-react";

import { useDirectoryList } from "../../../components/catalog/directory/useDirectoryList";
import { NEW_RECORD, useEditParam } from "../../../components/catalog/directory/useEditParam";
import { TagEditDialog } from "../../../components/catalog/directory/TagEditDialog";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";
import type { ListColumn } from "../../../components/ui/responsive-list-types";
import type { RowAction } from "../../../components/ui/RowActionsMenu";
import { useConfirm } from "../../../components/ui/use-confirm";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { catalogTags, type CatalogTag } from "../../../lib/api-catalog";
import { ApiError } from "../../../lib/api-client";
import { pluralRu } from "../../../lib/plural";

const productsText = (n: number) => `${n} ${pluralRu(n, ["товар", "товара", "товаров"])}`;

const TAG_COLUMNS: ListColumn<CatalogTag>[] = [
  { key: "name", header: "Название", cell: (t) => <span className="font-medium">{t.displayName}</span>, compact: "title" },
  {
    key: "products",
    header: "Товаров",
    width: "w-[120px]",
    align: "end",
    cell: (t) => <span className="tabular-nums text-on-surface-variant">{productsText(t.productsCount ?? 0)}</span>,
    compact: "trailing",
  },
];

export function TagsPage() {
  const notify = useNotify();
  const list = useDirectoryList(catalogTags.list, "Не удалось загрузить теги");
  const edit = useEditParam();
  const { confirm, request, settle } = useConfirm();
  const editing = edit.editId && !edit.isNew ? (list.items.find((t) => t.id === edit.editId) ?? null) : null;

  async function remove(t: CatalogTag) {
    const ok = await confirm({
      title: `Удалить тег «${t.displayName}»?`,
      description: (t.productsCount ?? 0) > 0 ? `Тег снимется с ${productsText(t.productsCount ?? 0)}.` : undefined,
      action: "Удалить",
      danger: true,
    });
    if (!ok) return;
    try {
      await catalogTags.remove(t.id);
      notify.success("Тег удалён");
      await list.refresh();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось удалить тег");
    }
  }

  const rowActions = (t: CatalogTag): RowAction[] => [
    { key: "edit", label: "Переименовать", icon: Pencil, run: () => edit.open(t.id) },
    { key: "delete", label: "Удалить", icon: Trash2, danger: true, run: () => void remove(t) },
  ];

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Теги"
        subtitle="Метки для подборок и фильтров на сайте"
        action={
          <Button variant="brand" onClick={() => edit.open(NEW_RECORD)}>
            <Plus className="size-4" />
            Тег
          </Button>
        }
      />
      <div className="w-full max-w-[960px]">
        <ResponsiveList
          aria-label="Теги"
          layout="compact"
          rows={list.items}
          columns={TAG_COLUMNS}
          rowKey={(t) => t.id}
          rowHref={(t) => edit.hrefFor(t.id)}
          rowLabel={(t) => `Тег ${t.displayName}`}
          rowActions={rowActions}
          rowActionsTitle={(t) => `Тег «${t.displayName}»`}
          loading={list.loading}
          error={list.error}
          onRetry={() => void list.refresh()}
          empty={{ icon: Tags, title: "Тегов пока нет", description: "Например: «без кофеина», «иммунитет» — по ним покупатели находят товары" }}
        />
      </div>
      <TagEditDialog
        open={edit.isNew || editing !== null}
        tag={editing}
        onClose={edit.close}
        onSaved={() => {
          edit.close();
          void list.refresh();
        }}
      />
      <ConfirmDialog request={request} onSettle={settle} />
    </div>
  );
}
